import { useMemo } from "react";
import { reset, SubmissionError } from "redux-form";
import type { CountryCode } from "libphonenumber-js";

import { fetchCurrentUser } from "@js/apps/auth/actions";
import { US_COUNTRY_CODE } from "@js/apps/common/constants";
import { useUser } from "@js/apps/common/hooks";
import { useUpdateFreelancerCustomizationMutation } from "@js/apps/freelancer/api";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch } from "@js/hooks/redux";

import { MOBILE_NUMBER_FORM_ID } from "./phone-verification-form";
import {
  PhoneVerificationForm,
  type PhoneVerificationFormValues,
} from "./phone-verification-form";

export const PhoneVerification = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const [updateFreelancerCustomization] =
    useUpdateFreelancerCustomizationMutation();

  const onSubmit = async (values: PhoneVerificationFormValues) => {
    await updateFreelancerCustomization({
      id: user?.freelancer as number,
      data: {
        user: {
          phone_number: values.phone_number,
        },
      },
    })
      .unwrap()
      .then(() => {
        Snackbar.success("Phone Number Successfully Updated.");
        void dispatch(fetchCurrentUser()).then(() => {
          dispatch(reset(MOBILE_NUMBER_FORM_ID));
        });
      })
      .catch((error) => {
        const phoneNumberError = error?.data?.user?.phone_number as
          Record<string, string[]> | undefined;
        const errorMessage =
          phoneNumberError?.phone_number || phoneNumberError?.country_iso2;
        if (errorMessage) {
          throw new SubmissionError({ phone_number: String(errorMessage) });
        }
        Snackbar.error("Sorry, something went wrong. Try again later.");
      });
  };

  const initialValues = useMemo(() => {
    if (!user) {
      return;
    }

    const initialCountryCode =
      user.phone_number?.country_iso2 ||
      user.address?.country ||
      US_COUNTRY_CODE;

    return {
      phone_number: {
        country_iso2: initialCountryCode as CountryCode,
        phone_number: user.phone_number?.phone_number || "",
      },
    };
  }, [user]);

  return (
    <PhoneVerificationForm onSubmit={onSubmit} initialValues={initialValues} />
  );
};
